import Vue from 'vue';
import { RootState } from '@/types/store';
import { Module } from 'vuex';
import * as Sentry from '@sentry/browser';
import { Blog } from '@/models/blog';
import { Confirmable } from '@/lib/script/confirmable';

export interface UserState {
  user_id: string;
  /** 用户基础信息 */
  basicInfo: User.BasicInfo | null;
  basicInfoMap: {
    [id: number]: User.BasicInfo | undefined;
  };
  stats: {
    [userId: number]: {
      total_distance: number | null;
      total_days: number | null;
      total_time: number | null;
    };
  };
  /** 设置 */
  setting: {
    enable_sign_in_alert: boolean;
    sign_in_alert_time: string;
  } | null;
  /** 用户信息 */
  personalInfo: User.PersonalInfo | null;
  // PersonalInfo | null;
  hasRecordNotify11: {
    area_code: string;
    mobile: string;
  } | null;
  creditRecord: ListWrap<User.CreditRecordItem> & ListRequesting;
  creditInfo: {
    count: number;
    remark: string;
  };
  moments: {
    [userId: number]: Blog[];
  };
  homeMedal: {
    [userId: number]: string[];
  };
  homeData: {
    [userId: number]: User.HomeData;
  };
  follow: ListWrap<number> & ListRequesting;
  followMap: { [userId: number]: User.FollowItem };
  fan: ListWrap<number> & ListRequesting;
  fanMap: { [userId: number]: User.FanItem };
  /** 是否已经关注userId */
  followInfo: {
    [userId: number]: boolean;
  };
  wthdrawalAlert: {
    url: string;
    intro: string;
    withdraws_channel: string;
  } | null;
  sentence: Array<{date: string; sentence: string}>
}

const m: Module<UserState, RootState> = {
  namespaced: true,
  state: () => ({
    user_id: '', // 用户id
    basicInfo: null,
    basicInfoMap: {},
    stats: {},
    setting: null,
    personalInfo: null,
    hasRecordNotify11: null,
    creditRecord: {
      list: [],
      requesting: false,
      loaded: false,
    },
    creditInfo: {
      count: 0,
      remark: '',
    },
    moments: {},
    homeMedal: {},
    homeData: {},
    follow: {
      list: [],
      loaded: false,
      requesting: false,
    },
    followMap: {},
    fan: {
      list: [],
      loaded: false,
      requesting: false,
    },
    fanMap: {},
    followInfo: {},
    wthdrawalAlert: null,
    sentence: []
  }),
  mutations: {
    /**
     * 记录用户基础信息
     */
    setBasicInfo(state, basicInfo) {
      state.basicInfo = basicInfo;
      if (basicInfo) {
        Sentry.configureScope((scope) => {
          scope.setUser({ id: basicInfo.id });
        });
        aplus_queue.push({
          action: 'aplus.setMetaInfo',
          arguments: ['_user_id', basicInfo.id],
        });
        // gio('setUserId', basicInfo.id);
        // gio('people.set', 'mobile_bound', basicInfo.mobile_bound ? '1' : '0');
      }
    },
    /**
     * 记录用户基础信息
     */
    setBasicInfoMap(state, { id, info }) {
      Vue.set(state.basicInfoMap, id, info);
    },
    setBasicInfoHasFollow(state, { userId, hasFollow }) {
      const info = state.basicInfoMap[userId];
      if (info) {
        info.has_follow = hasFollow;
      }
    },
    /**
     * 记录数据
     */
    setStats(state, { userId, stats }) {
      Vue.set(state.stats, userId, stats);
    },
    /**
     * 记录setting
     */
    setSetting(state, setting) {
      state.setting = setting;
    },
    /**
     * 记录personalInfo
     */
    setPersonalInfo(state, personalInfo) {
      state.personalInfo = personalInfo;
    },
    /**
     * 记录通知11上线
     */
    setRecordNotify11(state, notify11) {
      state.hasRecordNotify11 = notify11;
    },
    /**
     * 记录信用分记录列表
     * @param {} list
     */
    concatCreditRecord(state, list: User.CreditRecordItem) {
      state.creditRecord.list = state.creditRecord.list.concat(list);
    },
    setCreditRecordRequest(state, data) {
      state.creditRecord.requesting = data;
    },
    setCreditRecordLoaded(state, data) {
      state.creditRecord.loaded = data;
    },
    setCreditInfo(state, info) {
      state.creditInfo = info;
    },
    /**
     * 记录奖牌数据
     */
    setHomeMedal(state, { userId, medal }) {
      Vue.set(state.homeMedal, userId, medal);
    },
    /**
     * 记录关注/粉丝等数据
     */
    setHomeData(state, { userId, data }) {
      Vue.set(state.homeData, userId, data);
    },
    /**
     * 关注列表
     */
    initFollow(state) {
      state.follow.list = [];
      state.follow.loaded = false;
      state.follow.requesting = false;
    },
    concatFollow(state, { list }: { list: User.FollowItem[] }) {
      const ids: number[] = [];
      for (const item of list) {
        ids.push(item.id);
        item.follow = true;
        Vue.set(state.followMap, item.id, item);
      }
      state.follow.list.push(...ids);
    },
    setFollowLoaded(state, { loaded }) {
      state.follow.loaded = loaded;
    },
    setFollowReq(state, { req }) {
      state.follow.requesting = req;
    },
    setFollowHasFollow(state, { userId, follow }) {
      const item = state.followMap[userId];
      if (item) {
        item.follow = follow;
      }
    },
    /**
     * 粉丝列表
     */
    concatFan(state, { list }) {
      const ids: number[] = [];
      for (const item of list) {
        ids.push(item.id);
        Vue.set(state.fanMap, item.id, item);
      }
      state.fan.list.push(...ids);
    },
    setFanLoaded(state, { loaded }) {
      state.fan.loaded = loaded;
    },
    setWithdrawalAlert(state, wthdrawalAlert) {
      state.wthdrawalAlert = wthdrawalAlert;
    },
    setFanReq(state, { req }) {
      state.fan.requesting = req;
    },
    setFanHasFollow(state, { userId, hasFollow }) {
      const item = state.fanMap[userId];
      if (item) {
        item.has_follow = hasFollow;
      }
    },
    setFollowInfo(state, { userId, hasFollow }) {
      state.user_id = userId;
      Vue.set(state.followInfo, userId, hasFollow);
    },
    setSentence(state, sentence) {
        state.sentence = sentence
    }
  },
  actions: {
    /**
     * 获取用户基本数据
     * @param id 如果不传则获取自己的数据
     */
    async getBasicInfo({ state, commit }, { id, forceUpdate } = {}) {
      // 已有缓存
      const response = await Vue.axios.get('/api/v1/user/basic_info', {
        params: {
          id,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        if (!id) {
          commit('setBasicInfo', data);
        } else {
          commit(
            'user/setFollowInfo',
            {
              userId: data.id,
              hasFollow: data.has_follow,
            },
            { root: true },
          );
        }
        commit('setBasicInfoMap', {
          id,
          info: data,
        });
      }
      return rdata;
    },
    /**
     * 获取统计数据，例如总打卡天数，总距离
     */
    async getStats({ state, commit, rootState }, { userId } = {}) {
      if (!userId) {
        userId = rootState.user.basicInfo?.id;
      }
      const response = await Vue.axios.get('/api/v1/user/stats', {
        params: {
          id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setStats', {
          userId,
          stats: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取设置
     */
    async getSetting({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/user/settings');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setSetting', rdata.data);
      }
      return rdata;
    },
    /**
     * 更新设置
     */
    async updateSetting({ state, commit }, { key, value }) {
      const response = await Vue.axios.post('/api/v1/user/settings', {
        key,
        value,
      });
      return response.data;
    },
    /**
     * 实名认证申请
     */
    async initRealNameVerify({ state, commit }, { name, idCard, mobile }) {
      const response = await Vue.axios.post(
        '/api/v1/user/init_real_name_verify',
        {
          name,
          id_card: idCard,
          mobile,
        },
      );
      return response.data;
    },
    /**
     * 实名认证确认
     */
    async checkRealNameVerify({ dispatch }, { token, code }) {
      const response = await Vue.axios.post(
        '/api/v1/user/check_real_name_verify',
        {
          token,
          code,
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        await dispatch({
          type: 'getBasicInfo',
          forceUpdate: true,
        });
      }
      return rdata;
    },
    /**
     * 登录验证码
     */
    async getVerificateCode({ state, commit }, { areaCode, mobile }) {
      const response = await Vue.axios.post('/api/v1/user/bind_mobile', {
        area_code: areaCode,
        mobile,
      });
      return response.data;
    },
    /**
     * 绑定手机
     */
    async bindingPhone({ state, commit }, { serialNumber, code }) {
      const response = await Vue.axios.post('/api/v1/user/confirm_mobile', {
        serial_number: serialNumber,
        code,
      });
      return response.data;
    },
    /**
     * 绑定邀请人
     */
    async recordReferrer({ state, commit }, referrer) {
      const response = await Vue.axios.post('/api/v1/user/record_referrer', {
        user_id: referrer,
      });
      return response.data;
    },
    /**
     * 获取用户信息
     */
    async getPersonalInfo({ state, commit }, { forceUpdate } = {}) {
      if (state.personalInfo && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/user/user_info');
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        data.avatar = {
          key: data.avatar,
          url: data.avatar_url,
        };
        delete data.avatar_url;
        data.district = [
          data.district_1_id,
          data.district_2_id,
          data.district_3_id,
        ];
        delete data.district_1_id;
        delete data.district_2_id;
        delete data.district_3_id;
        commit('setPersonalInfo', new Confirmable(rdata.data));
      }
      return rdata;
    },
    /**
     * 获取用户信息
     * @param activity_id 选填
     */
    async updatePersonalInfo(
      { state, commit, dispatch },
      { activityId, forceUpdate } = {},
    ) {
      dispatch('getPersonalInfo');
      const info = state.personalInfo;
      const response = await Vue.axios.post('/api/v1/user/user_info', {
        activity_id: activityId,
        ...info,
        avatar: info?.avatar?.key,
        district_1_id: info?.district[0],
        district_2_id: info?.district[1],
        district_3_id: info?.district[2],
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        // @ts-ignore
        info.confirm();
      }
      return rdata;
    },
    /**
     * 记录通知11届
     */
    async recordNotify11(
      { state, commit },
      { mobile, areaCode, mobileBound, requireBound },
    ) {
      const response = await Vue.axios.post('/api/v1/user/record_notify_11', {
        mobile,
        area_code: areaCode,
        mobile_bound: mobileBound,
        require_bound: requireBound,
      });
      const rdata = response.data;
      return rdata;
    },
    async getRecordNotify11({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/user/record_notify_11');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setRecordNotify11', rdata.data);
      }
      return rdata;
    },

    /**
     * 获取用户信用分
     */
    async getCreditInfo({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/credit/info');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setCreditInfo', rdata.data);
      }
      return rdata;
    },
    /**
     * 获取用户信用分列表
     */
    async getCreditRecord(
      { state, commit },
      { lastId, count, forceUpdate = false },
    ) {
      const wrap = state.creditRecord;
      if (wrap.requesting || (wrap.loaded && !forceUpdate)) {
        return;
      }
      if (forceUpdate && wrap.list.length > 0) {
        lastId = undefined;
        wrap.loaded = false;
        wrap.list = [];
      }
      commit('setCreditRecordRequest', true);
      const response = await Vue.axios.get('/api/v1/credit/detail', {
        params: {
          last_id: lastId,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('concatCreditRecord', rdata.data);
        if (rdata.data.length < count) {
          commit('setCreditRecordLoaded', true);
        }
      }
      commit('setCreditRecordRequest', false);
      return rdata;
    },
    /**
     * 获取奖牌
     */
    async getHomeMedal(context, { userId } = {}) {
      if (!userId) {
        userId = context.rootState.user.basicInfo!.id;
      }
      const response = await Vue.axios.get('/api/v1/user/activity/medal', {
        params: {
          id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setHomeMedal', {
          userId,
          medal: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取关注/粉丝等数据
     */
    async getHomeData(context, { userId } = {}) {
      if (!userId) {
        userId = context.rootState.user.basicInfo!.id;
      }
      const response = await Vue.axios.get('/api/v1/user/home_data', {
        params: {
          id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setHomeData', {
          userId,
          data: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 关注
     */
    async follow(context, { userId }) {
      const response = await Vue.axios.post('/api/v1/user/follow', {
        id: userId,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setFollowInfo', {
          userId,
          hasFollow: true,
        });
      }
      return rdata;
    },
    /**
     * 取消关注
     */
    async cancelFollow(context, { userId }) {
      const response = await Vue.axios.post('/api/v1/user/cancel_follow', {
        id: userId,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setFollowInfo', {
          userId,
          hasFollow: false,
        });
      }
      return rdata;
    },
    /**
     * 获取关注列表
     */
    async getFollowList({ state, commit }, { offset, count, forceUpdate }) {
      const wrap = state.follow;
      if ((wrap.loaded && !forceUpdate) || wrap.requesting) {
        return;
      }
      if (forceUpdate) {
        wrap.list = [];
        offset = 0;
      }
      const end = offset + count;
      const len = wrap.list.length;
      if (end <= len) {
        return;
      }
      if (offset < len) {
        offset = len;
        count = end - offset;
      }
      commit('setFollowReq', { req: true });
      const response = await Vue.axios.get('/api/v1/user/follow', {
        params: {
          offset,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const list = rdata.data as User.FollowItem[];
        for (const item of list) {
          commit(
            'user/setFollowInfo',
            { userId: item.id, hasFollow: true },
            { root: true },
          );
        }
        commit('concatFollow', { list });
        if (list.length < count) {
          commit('setFollowLoaded', { loaded: true });
        }
      }
      commit('setFollowReq', { req: false });
      return rdata;
    },
    /**
     * 获取粉丝列表
     */
    async getFanList({ state, commit }, { offset, count, forceUpdate }) {
      const wrap = state.fan;
      if ((wrap.loaded && !forceUpdate) || wrap.requesting) {
        return;
      }
      if (forceUpdate) {
        wrap.list = [];
        offset = 0;
      }
      const end = offset + count;
      const len = wrap.list.length;
      if (end <= len) {
        return;
      }
      if (offset < len) {
        offset = len;
        count = end - offset;
      }
      commit('setFanReq', { req: true });
      const response = await Vue.axios.get('/api/v1/user/fans', {
        params: {
          offset,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const list = rdata.data as User.FanItem[];
        for (const item of list) {
          commit(
            'user/setFollowInfo',
            { userId: item.id, hasFollow: item.has_follow },
            { root: true },
          );
        }
        commit('concatFan', { list });
        if (list.length < count) {
          commit('setFanLoaded', { loaded: true });
        }
      }
      commit('setFanReq', { req: false });
      return rdata;
    },
    async getUserAlert() {
      const res = await Vue.axios.get('/api/v1/user/alert');
      const rdata = res.data;
      return rdata;
    },
    async getWithdrawalAlert({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/user/withdrawal_alert');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setWithdrawalAlert', rdata.data);
      }
      return rdata;
    },
    async addAlert() {
      const response = await Vue.axios.post('/api/v1/triangle/add_alert');
      const rdata = response.data;
      return rdata;
    },
    async getSentence(context, { date, activityId }) {
      const response = await Vue.axios.get('/api/v1/user/sentence', {
        params: { date, activity_id: activityId }
      });
      const rdata = response.data;
      if(rdata.status === 0) {
        context.commit('setSentence', rdata.data)
      }
      return rdata;
    },
    async updateSentence(context, { date, sentence, image, activityId }) {
      const response = await Vue.axios.post('/api/v1/user/sentence', {
        date, sentence, image, activity_id: activityId
      });
      const rdata = response.data;
      return rdata;
    },
    async certInfoStatus(context) {
      const response = await Vue.axios.get('/api/v1/user/cert_info_status');
      const rdata = response.data;
      return rdata;
    },
    async userCa(context) {
        const callback_url = location.origin + location.hash.split('?')[0] + '?ca=1'
        const response = await Vue.axios.get('/api/v1/user/ca',{
            params: {
                callback_url
            }
        });
      const rdata = response.data;
      return rdata;
    },
    async userCaStatus(context) {
      const response = await Vue.axios.get('/api/v1/user/ca_status');
      const rdata = response.data;
      return rdata;
    },
  },
};

export default m;
